/**
 * League-wide view: one row per player for the selected league-season.
 *
 * All filtering happens here, in plain JS over the fetched rows, rather than
 * through MUI's filter model. That's deliberate — "view" percentiles are ranked
 * over the rows on screen, so there must be exactly one thing deciding what's on
 * screen. A second, invisible filter model would make those percentiles lie.
 */

import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-premium";
import { useSearchParams } from "react-router-dom";
import { defaultSeason } from "./defaultSeason";

import {
  PlayerStatsGarden,
  PlayerStatsRow,
} from "../../../models/PlayerStats";
import { computeViewPercentiles, visibleColumns } from "./columns";
import FilterPanel from "./FilterPanel";
import {
  activeFilters,
  applyFilters,
  decodeFilters,
  IDENTITY_FILTER_FIELDS,
  encodeFilters,
  filterableColumns,
} from "./filters";
import PlayerIdentityCell from "./PlayerIdentityCell";
import { playerRouteSegment } from "./playerIds";
import PlayerStatsGrid from "./PlayerStatsGrid";
import PositionChips from "./PositionChips";
import { usePlayerStatsContext, seasonLabel } from "./PlayerStatsLayout";
import {
  EfficiencyMode,
  PercentileMode,
  PERCENTILE_MODE_LABELS,
} from "./PlayerStatsToolbar";
import SectionTabs, { useSelectedSection } from "./SectionTabs";
import { usePlayerStatsTable } from "./usePlayerStats";


export default function LeagueTable() {
  const { garden, sections, leagues, scopes, defaultLeague, loading } =
    usePlayerStatsContext();
  const [section, setSection] = useSelectedSection(sections);
  const [params, setParams] = useSearchParams();
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);

  // A league the current garden doesn't serve (a link copied across gardens)
  // falls back rather than fetching a scope that can't exist.
  const requestedLeague = params.get("league");
  const league =
    requestedLeague && leagues.includes(requestedLeague)
      ? requestedLeague
      : leagues.includes(defaultLeague)
        ? defaultLeague
        : leagues[0] ?? defaultLeague;

  const seasons = useMemo(
    () =>
      scopes
        .filter((s) => s.league === league)
        .map((s) => s.season)
        .sort((a, b) => b - a),
    [scopes, league],
  );

  const requestedSeason = Number(params.get("season"));
  const season = seasons.includes(requestedSeason)
    ? requestedSeason
    : defaultSeason(scopes, league) ?? seasons[0] ?? null;

  const search = params.get("q") ?? "";
  const position = params.get("pos") ?? "ALL";
  const team = params.get("team") ?? "ALL";
  const percentileMode = (params.get("mode") as PercentileMode) || "published";
  const columnFilters = useMemo(
    () => decodeFilters(params.get("filter")),
    [params],
  );

  const setParam = (updates: Record<string, string | null>) => {
    const updated = new URLSearchParams(params);
    Object.entries(updates).forEach(([key, value]) => {
      if (value == null || value === "" || value === "ALL" || value === "0") {
        updated.delete(key);
      } else {
        updated.set(key, value);
      }
    });
    setParams(updated, { replace: true });
  };

  // Team lives in the same popover as the column filters, so it counts as one of
  // them. Position doesn't — it has its own chips in the header bar, where its
  // state is already visible.
  const filterCount =
    activeFilters(columnFilters).length + (team !== "ALL" ? 1 : 0);

  // Identity columns ride every response, so only stat filters need requesting.
  const filterFields = useMemo(
    () =>
      columnFilters
        .filter((f) => !IDENTITY_FILTER_FIELDS.has(f.field))
        .map((f) => f.field),
    [columnFilters],
  );

  const {
    data,
    loading: tableLoading,
    error,
  } = usePlayerStatsTable(
    garden,
    league,
    season,
    section?.key ?? null,
    filterFields,
  );

  // Columns follow the section the response was built for, not the one just
  // requested. The grid keeps its rows through a refetch, so swapping headers on
  // click would show the new section's columns over the old section's values —
  // a table of em dashes for however long the request takes. This way it stays
  // coherent and swaps in one step when the rows land.
  //
  // Memoised because it feeds a useMemo dependency list — a fresh [] literal
  // each render would recompute view percentiles every time.
  const servedSection = useMemo(
    () => sections.find((s) => s.key === data?.section) ?? section,
    [sections, data?.section, section],
  );
  // Only one efficiency variant renders; the others ride in the payload unused.
  const efficiencyMode = (params.get("eff") as EfficiencyMode) || "ppp";
  const columns = useMemo(
    () => visibleColumns(servedSection?.columns ?? [], efficiencyMode),
    [servedSection, efficiencyMode],
  );
  // Prospect sections carry no variants at all, so there is nothing to toggle.
  const hasVariants = (servedSection?.columns ?? []).some((c) => c.variant);

  const sortModel: GridSortModel = useMemo(() => {
    const field = params.get("sort");
    if (!field) return [{ field: "poss", sort: "desc" }];
    return [{ field, sort: params.get("dir") === "asc" ? "asc" : "desc" }];
  }, [params]);

  const onSortModelChange = (model: GridSortModel) => {
    const next = model[0];
    setParam({
      sort: next?.field ?? null,
      dir: next?.sort === "asc" ? "asc" : null,
    });
  };

  // Same reason as `columns`: a fresh [] each render would invalidate every
  // downstream memo, including the view-percentile recompute over 560 rows.
  const allRows = useMemo(() => data?.rows ?? [], [data]);

  const teams = useMemo(
    () =>
      Array.from(
        new Set(
          allRows.flatMap((r) => (r.team ? r.team.split(", ") : [])),
        ),
      ).sort(),
    [allRows],
  );

  const filterColumns = useMemo(
    () => filterableColumns(sections, section?.key),
    [sections, section],
  );

  // The population the view is *about* — everything except the search box.
  // Searching is a lookup, not a filter: typing a name to find someone shouldn't
  // redefine the group he's being ranked against, or his percentiles would move
  // as you type and settle at 50 once only he is left.
  const population = useMemo(() => {
    const base = allRows.filter((row) => {
      if (position !== "ALL" && row.pos !== position) return false;
      // Team is a comma-joined list for traded players, so match a member of it
      // rather than the whole string.
      if (team !== "ALL" && !(row.team ?? "").split(", ").includes(team))
        return false;
      return true;
    });
    return applyFilters(base, columnFilters);
  }, [allRows, position, team, columnFilters]);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return population;
    return population.filter((row) => row.name.toLowerCase().includes(query));
  }, [population, search]);

  const viewPercentiles = useMemo(
    () =>
      percentileMode === "view"
        ? computeViewPercentiles(population, columns, (r) => r.playerId)
        : undefined,
    [percentileMode, population, columns],
  );

  const identityColumns = useMemo(
    () => buildIdentityColumns(league, garden),
    [league, garden],
  );

  const seasonIndex = season == null ? -1 : seasons.indexOf(season);
  const olderSeason = seasonIndex >= 0 ? seasons[seasonIndex + 1] : undefined;
  const newerSeason = seasonIndex > 0 ? seasons[seasonIndex - 1] : undefined;

  const sortLabel = useMemo(() => {
    const field = sortModel[0]?.field;
    const column = columns.find((c) => c.key === field);
    if (column) return `${column.group} · ${column.label}`;
    return field ? field.toUpperCase() : "";
  }, [sortModel, columns]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        flexWrap="wrap"
        useFlexGap
        sx={{
          px: 2,
          py: 0.85,
          backgroundColor: "#fff",
          border: "1px solid #dfe2e6",
          borderBottom: "none",
        }}
      >
        {loading ? (
          <Skeleton variant="rectangular" width={220} height={30} />
        ) : (
          <>
            <SeasonStepper
              season={season}
              onOlder={
                olderSeason
                  ? () => setParam({ season: String(olderSeason) })
                  : undefined
              }
              onNewer={
                newerSeason
                  ? () => setParam({ season: String(newerSeason) })
                  : undefined
              }
            />
            {garden !== "nba" && leagues.length > 1 && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <FieldLabel>League</FieldLabel>
                <Select
                  size="small"
                  value={league}
                  onChange={(e) =>
                    // Seasons differ by league — drop the old one and let the
                    // fallback pick the newest this league actually has.
                    setParam({ league: e.target.value, season: null })
                  }
                  sx={{ borderRadius: 0, minWidth: 140, fontSize: 13 }}
                >
                  {leagues.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            )}
            <PositionChips
              value={position}
              onChange={(next) => setParam({ pos: next })}
              label="Pos"
            />
          </>
        )}
      </Stack>

      <SectionTabs
        sections={sections}
        section={section}
        onChange={setSection}
        loading={loading}
      />

      <Popover
        open={!!filterAnchor}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { borderRadius: 0, mt: 0.5 } } }}
      >
        <FilterPanel
          columns={filterColumns}
          filters={columnFilters}
          onChange={(next) => setParam({ filter: encodeFilters(next) })}
          team={team}
          teams={teams}
          onTeamChange={(next) => setParam({ team: next })}
        />
      </Popover>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <PlayerStatsGrid
          rows={rows}
          columns={columns}
          identityColumns={identityColumns}
          getRowId={(row) => row.playerId}
          loading={tableLoading}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          percentileMode={percentileMode}
          onPercentileModeChange={(mode) => setParam({ mode })}
          viewPercentiles={viewPercentiles}
          onOpenFilters={setFilterAnchor}
          filtersOpen={!!filterAnchor}
          filterCount={filterCount}
          efficiencyMode={efficiencyMode}
          onEfficiencyModeChange={
            hasVariants
              ? (mode) => setParam({ eff: mode === "ppp" ? null : mode })
              : undefined
          }
          identityHeader={
            <TextField
              size="small"
              placeholder="Search players"
              value={search}
              onChange={(e) => setParam({ q: e.target.value })}
              // The grid header handles keys for navigation and would swallow
              // typing, and a click here must not trigger a column sort.
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <Search sx={{ fontSize: 14, color: "#8a8a8a" }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 0,
                  fontSize: 12.5,
                  backgroundColor: "#fff",
                  // Sized to the group-header strip it sits in (26px), not to
                  // MUI's small-input default of ~40px.
                  height: 22,
                  "& input": { padding: "0 4px" },
                  // The focus ring inherits `primary.main`, and a brand-red
                  // rectangle in a header full of red percentile chips reads as
                  // an error state rather than focus.
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#dfe2e6",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#c9ced4",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#8a8a8a",
                    borderWidth: "1px",
                  },
                },
              }}
              sx={{ width: "100%" }}
            />
          }
          exportFileName={`${garden}-${league}-${season}-${section?.key ?? ""}`}
        />
      )}

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          py: 0.85,
          backgroundColor: "#fff",
          border: "1px solid #dfe2e6",
          borderTop: "none",
        }}
      >
        <FooterText bold>
          Showing {rows.length} of {allRows.length} players
        </FooterText>
        <FooterText title={data?.scope.populationLabel ?? undefined}>
          {percentileMode === "off"
            ? "Percentiles hidden"
            : percentileMode === "view"
              ? `Percentiles ${PERCENTILE_MODE_LABELS.view} (n=${population.length})`
              : // Quote the roll-up's own population rather than assuming it's
                // this league-season: NBA percentiles are per season, but a
                // prospect's rank against every tracked prospect season at once.
                `Percentiles vs ${
                  data?.scope.populationLabel?.startsWith("all prospect")
                    ? "all tracked prospect seasons"
                    : `${league} ${seasonLabel(season)}`
                }` +
                (data?.scope.populationN ? ` (n=${data.scope.populationN})` : "")}
        </FooterText>
        <Box flexGrow={1} />
        {sortLabel && (
          <FooterText>
            Sorted by {sortLabel} {sortModel[0]?.sort === "asc" ? "↑" : "↓"}
          </FooterText>
        )}
      </Stack>
    </>
  );
}

/** Pinned identity block: rank, player, team + position, and the two sample
 *  columns every conversation about a stat immediately asks for. */
function buildIdentityColumns(
  league: string,
  garden: PlayerStatsGarden,
): GridColDef[] {
  // The player link carries the league explicitly, even when it's the default
  // and therefore absent from the URL — the player page opens scoped to the
  // league you were looking at, and can't infer it from a missing param.
  const playerSearch = () => {
    const search = new URLSearchParams(window.location.search);
    search.set("league", league);
    return `?${search.toString()}`;
  };

  return [
    {
      field: "rank",
      headerName: "Rk",
      width: 52,
      sortable: false,
      align: "left",
      headerAlign: "left",
      // Rank follows the grid's own sort, not the data — it's a position in the
      // current view, not a property of the player.
      renderCell: (params) => {
        const position = params.api.getSortedRowIds().indexOf(params.id);
        return (
          <span style={{ color: "#8a8a8a", fontWeight: 600 }}>
            {position >= 0 ? position + 1 : ""}
          </span>
        );
      },
    },
    {
      field: "name",
      headerName: "Player",
      width: 268,
      valueGetter: (_v, row: PlayerStatsRow) => row.name,
      renderCell: (params) => {
        const row = params.row as PlayerStatsRow;
        return (
          <PlayerIdentityCell
            row={row}
            to={`${playerRouteSegment(garden, row.playerId)}${playerSearch()}`}
            showHeadshot={garden === "nba"}
          />
        );
      },
    },
    {
      field: "gp",
      headerName: "GP",
      width: 58,
      type: "number",
      valueGetter: (_v, row: PlayerStatsRow) => row.gp,
    },
    {
      field: "poss",
      headerName: "Poss",
      width: 76,
      type: "number",
      valueGetter: (_v, row: PlayerStatsRow) => row.poss,
      valueFormatter: (value: number | null) =>
        typeof value === "number" ? value.toLocaleString("en-US") : "—",
    },
  ];
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.1em",
        color: "#8a8a8a",
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  );
}

function FooterText({
  children,
  bold,
  title,
}: {
  children: React.ReactNode;
  bold?: boolean;
  title?: string;
}) {
  return (
    <Typography
      title={title}
      sx={{
        fontSize: 11.5,
        fontWeight: bold ? 700 : 600,
        letterSpacing: bold ? "0.09em" : "0.04em",
        color: bold ? "#17191c" : "#8a8a8a",
      }}
    >
      {children}
    </Typography>
  );
}

function SeasonStepper({
  season,
  onOlder,
  onNewer,
}: {
  season: number | null;
  onOlder?: () => void;
  onNewer?: () => void;
}) {
  const arrow = (enabled: boolean) => ({
    width: 28,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: enabled ? "pointer" : "default",
    color: enabled ? "#3f4650" : "#c9ced4",
    userSelect: "none" as const,
  });

  return (
    <Stack direction="row" alignItems="center" border="1px solid #dfe2e6">
      <Box sx={arrow(!!onOlder)} onClick={onOlder}>
        <ChevronLeft fontSize="small" />
      </Box>
      <Typography
        sx={{
          minWidth: 82,
          textAlign: "center",
          fontFamily: "'Oswald', sans-serif",
          fontSize: 15,
          fontWeight: 500,
        }}
      >
        {seasonLabel(season)}
      </Typography>
      <Box sx={arrow(!!onNewer)} onClick={onNewer}>
        <ChevronRight fontSize="small" />
      </Box>
    </Stack>
  );
}
